import { API_URLS } from '@helpers/api';
import { withQueryParams } from '@hoc/withQueryParams';
import { useApi } from '@hooks/useApi';
import APLFilteredListing from '@stories/Components/APLFilteredListing/APLFilteredListing';
import MemberCard from '@stories/Components/Cards/HTAMemberCard/HTAMemberCard';
import InnerPageHero from '@stories/Widgets/InnerPageHero/InnerPageHero';
import queryString from 'query-string';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useFirstMountState } from 'react-use';
import S from './AplLandscaperListing.styles';
const AplLandscaperListing = ({ filterTitle, initialData, professionFilter, countyFilter, servicesFilter, title, randomSeed, errorText, sortByOptions, sustainabilityLink, }) => {
    const isFirstMount = useFirstMountState();
    /**
     * Manage query params via useLocation to ensure that multiple instances of a value being passed through
     * eg. ?profession=landscaper&profession=garden+designer&profession=professional+gardener
     * are handled correctly.
     *
     * useQueryParams either formats as an array, or returns only the first value
     */
    const location = useLocation();
    const history = useHistory();
    const [queryParams, setQueryParams] = React.useState(queryString.parse(location.search));
    const [shouldSendAPIRequest, setShouldSendAPIRequest] = React.useState(false);
    function updateQueryParams(params) {
        setQueryParams({ ...queryParams, ...params });
    }
    // Get additional filters.
    const name = queryParams.name ?? '';
    // const location = queryParams.location ?? '';
    // Get Search term
    const searchTerm = queryParams.searchTerm ?? '';
    // Get page index or default.
    const pageIndex = queryParams.pageIndex
        ? typeof queryParams.pageIndex === 'string'
            ? queryParams.pageIndex
            : queryParams.pageIndex[0]
        : '1';
    // Get sort by or default.
    const sortByValue = queryParams.sortBy
        ? typeof queryParams.sortBy === 'string'
            ? queryParams.sortBy
            : queryParams.sortBy[0]
        : sortByOptions[0].value;
    // Get profession values, and convert to array if required
    const professionValues = queryParams.profession
        ? typeof queryParams.profession === 'string'
            ? [queryParams.profession]
            : queryParams.profession
        : [];
    // Get county values, and convert to array if required
    const countyValues = queryParams.county
        ? typeof queryParams.county === 'string'
            ? [queryParams.county]
            : queryParams.county
        : [];
    // Get profession values, and convert to array if required
    const servicesValues = queryParams.services
        ? typeof queryParams.services === 'string'
            ? [queryParams.services]
            : queryParams.services
        : [];
    // Get postcode
    const postcodeValue = queryParams.postcode
        ? typeof queryParams.postcode === 'string'
            ? queryParams.postcode
            : queryParams.postcode[0]
        : '';
    const [postcodeModalData, setPostcodeModalData] = React.useState({
        show: false,
    });
    // Construct API url.
    const parameters = queryString.stringify({
        name: name,
        // location: location,
        pageIndex: pageIndex,
        pageSize: 12,
        searchTerm: searchTerm,
        profession: professionValues.join('+'),
        county: countyValues.join('+'),
        services: servicesValues.join('+'),
        randomSeed: randomSeed,
        sortBy: sortByValue,
        postcode: postcodeValue,
    });
    const apiUrl = `${API_URLS.landscapers}?${parameters}`;
    // Ensure page url is updated when queryParams change
    React.useEffect(() => history.push({ search: queryString.stringify(queryParams) }), [queryParams]);
    // Fetch data.
    const { data, loading, error } = useApi(apiUrl, shouldSendAPIRequest);
    React.useEffect(() => {
        if (isFirstMount) {
            return;
        }
        // Don't set it on first mount //
        setShouldSendAPIRequest(true);
    }, [queryParams]);
    React.useEffect(() => {
        if (shouldSendAPIRequest && !loading) {
            setShouldSendAPIRequest(false);
        }
    }, [shouldSendAPIRequest, loading]);
    // Get items from response or initial items.
    const items = data?.items ?? initialData.items;
    const totalItems = data?.totalItems ?? initialData.totalItems;
    return (React.createElement(React.Fragment, null,
        React.createElement(InnerPageHero, { title: title }),
        React.createElement(APLFilteredListing, { errorText: errorText, error: error, filtersProps: {
                alignment: 'left',
                professionValues,
                countyValues,
                servicesValues,
                professionFilters: professionFilter,
                countyFilters: countyFilter,
                servicesFilters: servicesFilter,
                title: filterTitle,
                onFilterChange: (key, val) => updateQueryParams({ [key]: val, pageIndex: '1' }),
                onSearchTermChange: (val) => updateQueryParams({ searchTerm: val, pageIndex: '1' }),
                searchTerm: searchTerm,
                onSortChange: (val) => {
                    updateQueryParams({ sortBy: val });
                },
                onPostcodeSearch: () => {
                    setPostcodeModalData({ show: true });
                },
                onPostcodeSubmit: (val) => {
                    updateQueryParams({ postcode: val, pageIndex: '1' });
                    setPostcodeModalData({ show: false });
                },
                sortByOptions: sortByOptions,
                sortByValue: sortByValue,
                showPostcodeModal: postcodeModalData.show,
                onPostcodeModalClose: () => {
                    setPostcodeModalData({ show: false });
                },
                postcodeValue,
                onClearPostcode: (reset) => {
                    if (reset) {
                        updateQueryParams({ postcode: '' });
                    }
                    else {
                        updateQueryParams({ postcode: '' });
                        setPostcodeModalData({ show: true });
                    }
                },
                onClearSelected: (value) => {
                    const profession = professionFilter.options.filter((x) => x.value === value).length > 0;
                    const county = countyFilter.options.filter((x) => x.value === value).length > 0;
                    const services = servicesFilter.options.filter((x) => x.value === value).length > 0;
                    if (profession) {
                        updateQueryParams({
                            [professionFilter.key]: professionValues.filter((x) => x !== value),
                        });
                    }
                    else if (county) {
                        updateQueryParams({
                            [countyFilter.key]: countyValues.filter((x) => x !== value),
                        });
                    }
                    else if (services) {
                        updateQueryParams({
                            [servicesFilter.key]: servicesValues.filter((x) => x !== value),
                        });
                    }
                    else {
                        return;
                    }
                },
                onClearAllFilters: () => updateQueryParams({
                    [professionFilter.key]: [],
                    [countyFilter.key]: [],
                    [servicesFilter.key]: [],
                    postcode: '',
                    sortBy: '',
                }),
            }, items: items, loading: loading, pageIndex: parseInt(pageIndex), renderItems: (items) => (React.createElement(S.ItemList, null, items.map((item) => (React.createElement(S.Item, { key: item.id },
                React.createElement(MemberCard, { ...item, showButton: !!item.url, sustainabilityLink: sustainabilityLink })))))), totalItems: totalItems, onPageChange: (pageIndex) => {
                updateQueryParams({ pageIndex: pageIndex.toString() });
            } })));
};
export default withQueryParams(AplLandscaperListing);
